import { useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import ApartmentOutlined from "@mui/icons-material/ApartmentOutlined";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { COST_PAGES, MOCK_CLOSINGS } from "../data/mockData";
import { GestaoCustosProvider, useGestaoCustos } from "../GestaoCustosContext";
import OperationsTheme from "../../../components/shared/OperationsTheme";

function ProductNavigation() {
  const { projects, projectId, setProjectId, closingId, setClosingId, resetDemo } = useGestaoCustos();
  const location = useLocation();
  const closingOptions = MOCK_CLOSINGS;
  const activePage = useMemo(() => {
    if (location.pathname.includes("/estoque-compras/")) return "estoque-compras/controle";
    const suffix = location.pathname.replace(/^\/gestao-custos\/?/, "");
    return suffix || "visao-geral";
  }, [location.pathname]);

  return (
    <Paper elevation={0} sx={{ border: "1px solid var(--ops-border)", borderRadius: 2, overflow: "hidden", mb: 1.25, bgcolor: "var(--ops-nav)" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={{ xs: 0.6, md: 0.8 }}
        sx={{ px: { xs: 0.8, md: 1.1 }, py: 0.3, bgcolor: "var(--ops-nav)" }}
      >
        <Box
          component="nav"
          aria-label="Páginas de Gestão de Custos"
          sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, flexWrap: "wrap" }}
        >
          {COST_PAGES.map((item) => {
            const active = activePage === item.path;
            return (
              <Button
                key={item.path}
                component={NavLink}
                to={`/gestao-custos/${item.path}`}
                color="inherit"
                aria-current={active ? "page" : undefined}
                sx={{
                  position: "relative",
                  minWidth: "max-content",
                  px: { xs: 0.7, md: 0.6, lg: 0.75 },
                  py: 0.65,
                  borderRadius: 0,
                  color: active ? "var(--ops-accent)" : "var(--ops-text)",
                  fontWeight: active ? 800 : 600,
                  fontSize: 12.5,
                  whiteSpace: "nowrap",
                  "&::after": active ? {
                    content: '""', position: "absolute", height: 3, left: 10, right: 10, bottom: 0,
                    borderRadius: "3px 3px 0 0", bgcolor: "var(--ops-accent)",
                  } : {},
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ flexShrink: 0, pb: { xs: 0.3, md: 0 } }}>
          <FormControl size="small" sx={{ width: { xs: 190, sm: 202 } }}>
            <Select
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              aria-label="Projeto"
              renderValue={(value) => (
                <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  <ApartmentOutlined sx={{ fontSize: 17, color: "var(--ops-accent)" }} />
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{projects.find((project) => project.id === value)?.name}</Box>
                </Stack>
              )}
              sx={{ bgcolor: "var(--ops-surface)", fontSize: 10.5, color: "var(--ops-text)", height: 34, "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--ops-input-border)" }, "& .MuiSelect-select": { py: 0.8, pl: 1, pr: 3.5 } }}
            >
              {projects.map((project) => <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: { xs: 112, sm: 120 } }}>
            <Select
              value={closingId}
              onChange={(event) => setClosingId(event.target.value)}
              aria-label="Mês e ano"
              renderValue={(value) => (
                <Stack direction="row" spacing={0.7} alignItems="center" sx={{ whiteSpace: "nowrap" }}>
                  <CalendarMonthOutlined sx={{ fontSize: 17, color: "var(--ops-accent)" }} />
                  {closingOptions.find((closing) => closing.id === value)?.label}
                </Stack>
              )}
              sx={{ bgcolor: "var(--ops-surface)", fontSize: 10.5, color: "var(--ops-text)", height: 34, "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--ops-input-border)" }, "& .MuiSelect-select": { py: 0.8, pl: 0.8, pr: 3 } }}
            >
              {closingOptions.map((closing) => <MenuItem key={closing.id} value={closing.id}>{closing.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Tooltip title="Restaurar dados simulados iniciais">
            <Button onClick={resetDemo} aria-label="Restaurar dados simulados" sx={{ minWidth: 32, width: 32, height: 36, px: 0, color: "var(--ops-muted)" }}>
              <RestartAltRounded fontSize="small" />
            </Button>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

function GestaoCustosContent() {
  return (
    <Box sx={{
      mx: { xs: -2, md: -3 },
      my: -3,
      px: { xs: 2, md: 3 },
      pt: { xs: 1.25, md: 1.6 },
      pb: 2,
      minHeight: "100%",
      color: "var(--ops-text)",
      background: "linear-gradient(180deg, var(--ops-canvas) 0%, var(--ops-canvas-end) 100%)",
    }}>
      <ProductNavigation />
      <Box sx={{
        minWidth: 0,
        "& .MuiButton-root": { fontSize: 11.5, minHeight: 30, fontWeight: 650, boxShadow: "none" },
        "& .MuiChip-root": { height: 23, fontSize: 10.5, maxWidth: "100%" },
        "& .MuiInputBase-root": { fontSize: 12 },
        "& .MuiSelect-select": { py: 0.85 },
        "& .MuiInputLabel-root": { fontSize: 12 },
        "& .MuiAlert-root": { py: 0.5, px: 1.25, fontSize: 11.5, borderRadius: 1.5 },
        "& .MuiAlert-message": { py: 0.5 },
        "& .MuiAlert-icon": { fontSize: 18, py: 0.5, mr: 1 },
      }}>
        <Outlet />
      </Box>
    </Box>
  );
}

export default function GestaoCustosShell() {
  return (
    <OperationsTheme><GestaoCustosProvider>
      <GestaoCustosContent />
    </GestaoCustosProvider></OperationsTheme>
  );
}
